import type { PossibleTypesMap } from '@apollo/client/cache'

/**
 * Fragment matching for every abstract type in the Mapped schema.
 *
 * WHY THIS FILE EXISTS AT ALL. Without a `possibleTypes` map an
 * `InMemoryCache` cannot tell that a stored `Building` satisfies
 * `... on Place`, so it falls back to exact `__typename` equality when it
 * reads a union or interface selection back out. The failure that produces is
 * the nasty kind: the first render, served straight from the network response,
 * is correct; the second render, served from the cache, silently drops every
 * field that sat under an inline fragment. A row's location disappears, a
 * provenance list that had three contributors a second ago shows none, and
 * nothing anywhere logs an error. It reads as a backend flake and gets triaged
 * as one.
 *
 * The Explore panel selects unions everywhere — `hasLocation`, `isPointOf`,
 * `relatesTo`, `identities`, `isFedBy`/`feeds`/`serves`/`controls` and
 * `hasPropertyContributedFromEdges.node` — so this is a precondition of the
 * feature, not a tuning knob.
 *
 * WHY IT IS THE WHOLE SCHEMA RATHER THAN THE TYPES WE USE TODAY. A map covering
 * only the abstract types the current documents touch is a trap: the next
 * relationship section added to the panel silently reintroduces the bug, and
 * the symptom shows up in a different feature from the change that caused it.
 * The full map is 74 entries / 693 members of static strings — a few kilobytes
 * — which is a trade worth making once.
 *
 * REGENERATING IT. Derived mechanically from the introspection dump that ships
 * inside `@mapped/schema-graph-react-apollo`, which is the same pinned schema
 * `queries.test.ts` validates documents against, so the two never disagree
 * about what a union contains. `possibleTypes.test.ts` regenerates the map
 * from that dump and asserts deep equality, so a dependency bump that adds a
 * union member fails the test rather than degrading a cache read months later.
 * When it does fail, re-run the generator the test prints and paste the result.
 * Last regenerated against `@mapped/schema-graph-react-apollo@3.0.0`, the
 * release that split `HasExternalReferenceUnion` into `PointExternalReference`
 * and `ThingExternalReference` and added the OPCUA types.
 */
export const possibleTypes: PossibleTypesMap = {
  AccessCredentialIdentityModel: ['AccessCredentialIdentity'],
  AccessCredentialIdentityUnion: ['AccessCredentialIdentity'],
  AccountIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  AgentIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  BACnetObjectIdModel: ['BACnetObjectId'],
  BACnetVendorIdModel: ['BACnetVendorId'],
  BuildingHasPropertyContributedFromUnion: [
    'Building',
    'Floor',
    'GenericPlace',
    'NameIdentity',
    'PostalAddressIdentity',
    'PropertyBag',
    'Site',
    'Space',
    'SubBuilding',
    'Zone',
  ],
  BuildingIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  Calendar: ['GenericCalendar', 'SpaceBookingCalendar'],
  CalendarEventIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  CalendarInvitationIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  CalendarInvitee: ['Person', 'Space'],
  CollectionHasPropertyContributedFromUnion: [
    'Collection',
    'NameIdentity',
    'PostalAddressIdentity',
    'PropertyBag',
  ],
  CollectionIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  ControlsUnion: ['Collection', 'Thing'],
  DeviceModelIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  Edge: [
    'ContributesPropertyEdge',
    'ControlsEdge',
    'FeedsEdge',
    'GeolocationEdge',
    'GradeTransitionLowerElevationEdge',
    'GradeTransitionUpperElevationEdge',
    'GradeTransitionVerticalFaceAreaEdge',
    'GrossAreaEdge',
    'HasAccessCredentialEdge',
    'HasActualCostEdge',
    'HasAddressEdge',
    'HasAssignedGroupEdge',
    'HasAssigneeEdge',
    'HasAttendeeEdge',
    'HasBillEdge',
    'HasCalendarEdge',
    'HasCalendarEventEdge',
    'HasDerivationEdge',
    'HasDeviceModelEdge',
    'HasEstimatedCostEdge',
    'HasExtendedPropertyEdge',
    'HasExternalReferenceEdge',
    'HasFileEdge',
    'HasHeartbeatEdge',
    'HasJointChildEdge',
    'HasJointEdge',
    'HasJointParentEdge',
    'HasLocationEdge',
    'HasMemberEdge',
    'HasPartEdge',
    'HasPlannedCostEdge',
    'HasPointEdge',
    'HasPrimBindingEdge',
    'HasPrimaryContactEdge',
    'HasProviderEdge',
    'HasUpstreamHeartbeatEdge',
    'HostsEdge',
    'IsAdjacentToEdge',
    'IsBilledToEdge',
    'IsCalendarOfEdge',
    'IsClosedByEdge',
    'IsCompletedByEdge',
    'IsControlledByEdge',
    'IsCreatedByEdge',
    'IsDerivedFromEdge',
    'IsFedByEdge',
    'IsHeartbeatOfEdge',
    'IsHostedByEdge',
    'IsJointChildOfEdge',
    'IsJointOfEdge',
    'IsJointParentOfEdge',
    'IsLocationOfEdge',
    'IsMemberOfEdge',
    'IsPartOfEdge',
    'IsPointOfEdge',
    'IsReferencedByLayerEdge',
    'IsReportedByEdge',
    'IsResponsibilityOfEdge',
    'IsResponsibleForEdge',
    'IsServedByEdge',
    'IsUpstreamHeartbeatOfEdge',
    'ManufacturedByEdge',
    'ReferencesLayerEdge',
    'RelatesToEdge',
    'RentableAreaEdge',
    'ResolvesInLayerEdge',
    'ResolvesPrimBindingEdge',
    'ServesEdge',
    'UsableAreaEdge',
  ],
  EmailAddressIdentityModel: ['EmailAddressIdentity'],
  EmailAddressIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  EmailIdentityModel: ['EmailIdentity'],
  Entity: [
    'AccessCredential',
    'Account',
    'Agent',
    'BACnetAnalogInputObject',
    'BACnetAnalogOutputObject',
    'BACnetAnalogValueObject',
    'BACnetBinaryInputObject',
    'BACnetBinaryOutputObject',
    'BACnetBinaryValueObject',
    'BACnetDeviceObject',
    'BACnetMultistateInputObject',
    'BACnetMultistateOutputObject',
    'BACnetMultistateValueObject',
    'Building',
    'CalendarEvent',
    'CalendarInvitation',
    'Collection',
    'DeviceModel',
    'EmailAddress',
    'Floor',
    'GenericCalendar',
    'GenericPlace',
    'Heartbeat',
    'Lease',
    'OPCUADataType',
    'OPCUAEnumValue',
    'OPCUAField',
    'OPCUAObject',
    'OPCUAObjectType',
    'OPCUAProperty',
    'OPCUAServer',
    'OPCUAVariable',
    'OPCUAVariableType',
    'OpenUSDLODDistanceHeuristic',
    'OpenUSDLODScreenSizeHeuristic',
    'OpenUSDLayer',
    'OpenUSDPrimBinding',
    'Organization',
    'PeopleGroup',
    'Person',
    'PhoneNumber',
    'Point',
    'PrismaticJoint',
    'RevoluteJoint',
    'ServiceRequest',
    'Site',
    'Space',
    'SpaceBookingCalendar',
    'SubBuilding',
    'Thing',
    'WorkOrder',
    'Zone',
  ],
  Event: ['CalendarEvent'],
  ExternalIdentityModel: ['ExternalIdentity'],
  FeedsUnion: [
    'Building',
    'Collection',
    'Floor',
    'GenericPlace',
    'Site',
    'Space',
    'SubBuilding',
    'Thing',
    'Zone',
  ],
  FloorHasPropertyContributedFromUnion: [
    'Building',
    'Floor',
    'GenericPlace',
    'NameIdentity',
    'PostalAddressIdentity',
    'PropertyBag',
    'Site',
    'Space',
    'SubBuilding',
    'Zone',
  ],
  FloorIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  FloorLevelIdentityModel: ['FloorLevelIdentity'],
  GenericPlaceIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
  ],
  GraphNode: [
    'AccessCredential',
    'Account',
    'Agent',
    'BACnetAnalogInputObject',
    'BACnetAnalogOutputObject',
    'BACnetAnalogValueObject',
    'BACnetBinaryInputObject',
    'BACnetBinaryOutputObject',
    'BACnetBinaryValueObject',
    'BACnetDeviceObject',
    'BACnetMultistateInputObject',
    'BACnetMultistateOutputObject',
    'BACnetMultistateValueObject',
    'Building',
    'CalendarEvent',
    'CalendarInvitation',
    'Collection',
    'DeviceModel',
    'EmailAddress',
    'Floor',
    'GenericCalendar',
    'GenericPlace',
    'Heartbeat',
    'Lease',
    'OPCUADataType',
    'OPCUAEnumValue',
    'OPCUAField',
    'OPCUAObject',
    'OPCUAObjectType',
    'OPCUAProperty',
    'OPCUAServer',
    'OPCUAVariable',
    'OPCUAVariableType',
    'OpenUSDLODDistanceHeuristic',
    'OpenUSDLODScreenSizeHeuristic',
    'OpenUSDLayer',
    'OpenUSDPrimBinding',
    'Organization',
    'PeopleGroup',
    'Person',
    'PhoneNumber',
    'Point',
    'PrismaticJoint',
    'RevoluteJoint',
    'ServiceRequest',
    'Site',
    'Space',
    'SpaceBookingCalendar',
    'SubBuilding',
    'Thing',
    'WorkOrder',
    'Zone',
  ],
  HasPartUnion: [
    'Building',
    'Collection',
    'Floor',
    'GenericPlace',
    'Point',
    'Site',
    'Space',
    'SubBuilding',
    'Thing',
    'Zone',
  ],
  HasPropertyContributedFromUnion: [
    'NameIdentity',
    'PostalAddressIdentity',
    'PropertyBag',
    'Thing',
  ],
  HeartbeatHasPropertyContributedFromUnion: [
    'NameIdentity',
    'Point',
    'PostalAddressIdentity',
    'PropertyBag',
  ],
  HeartbeatIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  Identity: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  IsCalendarOfUnion: ['Person', 'Space'],
  IsFedByUnion: ['Collection', 'Thing'],
  IsHeartbeatOfUnion: ['Point', 'Thing'],
  IsLocationOfUnion: ['Collection', 'Thing'],
  IsPointOfUnion: [
    'Building',
    'Collection',
    'Floor',
    'GenericPlace',
    'PrismaticJoint',
    'RevoluteJoint',
    'Site',
    'Space',
    'SubBuilding',
    'Thing',
    'Zone',
  ],
  IsPrimBindingOfUnion: [
    'AccessCredential',
    'Account',
    'Agent',
    'Building',
    'CalendarEvent',
    'CalendarInvitation',
    'Collection',
    'DeviceModel',
    'EmailAddress',
    'Floor',
    'GenericCalendar',
    'GenericPlace',
    'Heartbeat',
    'Lease',
    'Organization',
    'PeopleGroup',
    'Person',
    'PhoneNumber',
    'Point',
    'PrismaticJoint',
    'RevoluteJoint',
    'ServiceRequest',
    'Site',
    'Space',
    'SpaceBookingCalendar',
    'SubBuilding',
    'Thing',
    'WorkOrder',
    'Zone',
  ],
  KinematicJoint: ['PrismaticJoint', 'RevoluteJoint'],
  LeaseIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  NameIdentityModel: ['NameIdentity'],
  OPCUADataTypeIsDataTypeOfUnion: [
    'OPCUAField',
    'OPCUAProperty',
    'OPCUAVariable',
  ],
  OPCUANode: [
    'OPCUADataType',
    'OPCUAObject',
    'OPCUAObjectType',
    'OPCUAProperty',
    'OPCUAVariable',
    'OPCUAVariableType',
  ],
  OPCUANodeOrganizedByUnion: ['OPCUAObject', 'OPCUAServer'],
  OpenUSDLODHeuristic: [
    'OpenUSDLODDistanceHeuristic',
    'OpenUSDLODScreenSizeHeuristic',
  ],
  OrganizationIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  PeopleGroupIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  PersonIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  PhoneNumberIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  Place: [
    'Building',
    'Floor',
    'GenericPlace',
    'Site',
    'Space',
    'SubBuilding',
    'Zone',
  ],
  PointExternalReference: [
    'BACnetAnalogInputObject',
    'BACnetAnalogOutputObject',
    'BACnetAnalogValueObject',
    'BACnetBinaryInputObject',
    'BACnetBinaryOutputObject',
    'BACnetBinaryValueObject',
    'BACnetMultistateInputObject',
    'BACnetMultistateOutputObject',
    'BACnetMultistateValueObject',
    'OPCUAVariable',
  ],
  PointHasPropertyContributedFromUnion: [
    'NameIdentity',
    'Point',
    'PostalAddressIdentity',
    'PropertyBag',
  ],
  PointIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  PostalAddressIdentityModel: ['PostalAddressIdentity'],
  RelatesToUnion: [
    'Building',
    'Floor',
    'GenericPlace',
    'ServiceRequest',
    'Site',
    'Space',
    'SubBuilding',
    'Thing',
    'Zone',
  ],
  ServesUnion: [
    'Building',
    'Floor',
    'GenericPlace',
    'Organization',
    'Site',
    'Space',
    'SubBuilding',
    'Thing',
    'Zone',
  ],
  ServiceRequestIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  SiteHasPropertyContributedFromUnion: [
    'Building',
    'Floor',
    'GenericPlace',
    'NameIdentity',
    'PostalAddressIdentity',
    'PropertyBag',
    'Site',
    'Space',
    'SubBuilding',
    'Zone',
  ],
  SiteIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  SpaceBookingCalendarIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  SpaceCodeModel: ['SpaceCode'],
  SpaceHasPropertyContributedFromUnion: [
    'Building',
    'Floor',
    'GenericPlace',
    'NameIdentity',
    'PostalAddressIdentity',
    'PropertyBag',
    'Site',
    'Space',
    'SubBuilding',
    'Zone',
  ],
  SpaceIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  SubBuildingCodeModel: ['SubBuildingCode'],
  SubBuildingHasPropertyContributedFromUnion: [
    'Building',
    'Floor',
    'GenericPlace',
    'NameIdentity',
    'PostalAddressIdentity',
    'PropertyBag',
    'Site',
    'Space',
    'SubBuilding',
    'Zone',
  ],
  SubBuildingIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  ThingContainer: [
    'Building',
    'Floor',
    'GenericPlace',
    'Site',
    'Space',
    'SubBuilding',
    'Zone',
  ],
  ThingExternalReference: [
    'BACnetDeviceObject',
    'OPCUAObject',
    'OPCUAServer',
    'OPCUAVariable',
  ],
  ThingHasPropertyContributedFromUnion: [
    'NameIdentity',
    'PostalAddressIdentity',
    'PropertyBag',
    'Thing',
  ],
  ThingIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  WorkOrderIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
  ZoneHasPropertyContributedFromUnion: [
    'Building',
    'Floor',
    'GenericPlace',
    'NameIdentity',
    'PostalAddressIdentity',
    'PropertyBag',
    'Site',
    'Space',
    'SubBuilding',
    'Zone',
  ],
  ZoneIdentityUnion: [
    'AccessCredentialIdentity',
    'BACnetObjectId',
    'BACnetVendorId',
    'EmailAddressIdentity',
    'EmailIdentity',
    'ExternalIdentity',
    'FloorLevelIdentity',
    'GenericIdentity',
    'NameIdentity',
    'PostalAddressIdentity',
    'SpaceCode',
    'SubBuildingCode',
  ],
}
